import type { ContactVersion } from '@common/types/contacts';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import z from 'zod';

interface Props {
    contact: ContactVersion;
    onClose: () => void;
    onSave: (contact: ContactVersion) => Promise<void>;
    onRemove?: () => void;
}

const EDITABLE_FIELDS = [
    'firstName',
    'middleName',
    'lastName',
    'contactEmail',
    'phoneNumber',
] as const;

const CONTACT_SCHEMA = z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    contactEmail: z.email('Email must be valid'),
});

export default function EditContactModal({ contact, onClose, onSave, onRemove }: Props) {
    const [draftContact, setDraftContact] = useState<ContactVersion>(contact);
    const [errors, setErrors] = useState<Record<string, string | undefined>>({});
    const [isSaving, setIsSaving] = useState(false);

    const hasChanges = useMemo(
        () =>
            EDITABLE_FIELDS.some((field) => (draftContact[field] ?? '') !== (contact[field] ?? '')),
        [draftContact, contact],
    );

    const getFieldErrors = (contactToValidate: ContactVersion) => {
        const validationResult = CONTACT_SCHEMA.safeParse(contactToValidate);

        if (validationResult.success) return {};

        const fieldErrors: Record<string, string> = {};
        for (const issue of validationResult.error.issues) {
            const field = issue.path[0];
            if (typeof field === 'string' && !fieldErrors[field]) {
                fieldErrors[field] = issue.message;
            }
        }

        return fieldErrors;
    };

    const handleFieldChange = (field: keyof ContactVersion, value: string) => {
        setDraftContact((prev) => {
            const nextContact = { ...prev, [field]: value } as ContactVersion;
            setErrors(getFieldErrors(nextContact));

            return nextContact;
        });
    };

    const handleSave = async () => {
        const fieldErrors = getFieldErrors(draftContact);
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }

        setErrors({});
        setIsSaving(true);
        try {
            await onSave(draftContact);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit contact</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="First name"
                            required
                            fullWidth
                            value={draftContact?.firstName ?? ''}
                            onChange={(event) => handleFieldChange('firstName', event.target.value)}
                            error={Boolean(errors.firstName)}
                            helperText={errors.firstName}
                        />
                        <TextField
                            label="Middle name"
                            fullWidth
                            value={draftContact?.middleName ?? ''}
                            onChange={(event) =>
                                handleFieldChange('middleName', event.target.value)
                            }
                        />
                        <TextField
                            label="Last name"
                            required
                            fullWidth
                            value={draftContact?.lastName ?? ''}
                            onChange={(event) => handleFieldChange('lastName', event.target.value)}
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName}
                        />
                    </Stack>
                    <TextField
                        label="Email"
                        required
                        fullWidth
                        value={draftContact?.contactEmail ?? ''}
                        onChange={(event) => handleFieldChange('contactEmail', event.target.value)}
                        error={Boolean(errors.contactEmail)}
                        helperText={errors.contactEmail}
                    />
                    <TextField
                        label="Cell number"
                        fullWidth
                        value={draftContact?.phoneNumber ?? ''}
                        onChange={(event) => handleFieldChange('phoneNumber', event.target.value)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    justifyContent: onRemove ? 'space-between' : undefined,
                }}
            >
                {onRemove && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={onRemove}
                        disabled={isSaving}
                        sx={{
                            color: 'error.main',
                            '&:disabled': {
                                color: 'error.main',
                                borderColor: 'error.main',
                            },
                        }}
                    >
                        Remove
                    </Button>
                )}
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges || Object.keys(errors).length > 0}
                    >
                        Save
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
